import { useTranslation } from "react-i18next";
import { AppCard } from "@/components/ui";
import { ZapOff, ShieldOff, Target, Layers } from "lucide-react";

const PAIN_ICONS = [ZapOff, ShieldOff, Target];

/**
 * Problem/comparison section — highlights pain points with a solution card.
 * Two-column layout: pain points (left) + Neural solution card (right).
 * Rosé Pine Dawn luxury aesthetic.
 */
export function ProblemSection() {
  const { t } = useTranslation();

  const items = t("landing.problem.items", { returnObjects: true }) as {
    title: string;
  }[];

  const cards = t("landing.problem.cards", { returnObjects: true }) as {
    label: string;
    description: string;
  }[];

  return (
    <section id="problem" className="py-24 px-6 bg-surface/80">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        {/* Pain points column */}
        <div className="space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold leading-tight">
            {t("landing.problem.heading").replace(
              t("landing.problem.headingHighlight"),
              "",
            )}
            <span className="text-rose">
              {t("landing.problem.headingHighlight")}
            </span>
          </h2>
          <p className="text-lg text-foreground-secondary leading-relaxed">
            {t("landing.problem.description")}
          </p>
          <div className="space-y-4">
            {items.map((item, i) => {
              const Icon = PAIN_ICONS[i] ?? Target;
              return (
                <div key={i} className="flex items-center gap-4 text-foreground-muted">
                  <Icon className="w-5 h-5 text-rose shrink-0" />
                  <span className="text-sm font-medium">{item.title}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Solution card */}
        <AppCard className="bg-background p-10 rounded-[3rem] border border-border shadow-xl overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-success/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          <div className="relative z-10 space-y-6">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-success rounded-2xl flex items-center justify-center shadow-lg">
                <Layers className="text-background w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">
                {t("landing.problem.cardTitle")}
              </h3>
            </div>
            <div className="space-y-6">
              {cards.map((card, i) => (
                <div
                  key={i}
                  className="p-5 bg-surface rounded-2xl border border-success/10 shadow-sm"
                >
                  <p className="text-xs font-bold text-success uppercase mb-2">
                    {card.label}
                  </p>
                  <p className="text-sm">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </AppCard>
      </div>
    </section>
  );
}
